import { useEffect, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { inquiryApi, type InquiryAssigneeListResponse } from "@bw/shared/api";
import type { AssigneePickerProps } from "./assignee-picker.types";

export function AssigneePicker(props: AssigneePickerProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        size="small"
        disabled={props.disabled}
        onClick={() => setOpen(true)}
      >
        {props.assignedUserId ? "Reassign" : "Assign"}
      </Button>
      {open ? (
        <Choices
          key={`${props.actorId}:${props.conversationId}:${props.revision}`}
          {...props}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function Choices({
  actorId,
  conversationId,
  revision,
  disabled,
  onAssign,
  onAuthorityError,
  onClose,
}: AssigneePickerProps & { onClose: () => void }) {
  const reported = useRef(0);
  const cursors = useRef(new Set<string>());
  const directory = useInfiniteQuery({
    queryKey: ["business-inquiry-assignees", actorId, conversationId, revision],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const page = await inquiryApi.listAssignees(
        conversationId,
        { ...(pageParam ? { cursor: pageParam } : {}) },
        signal,
      );
      if (
        page.conversation_id !== conversationId ||
        page.revision !== revision ||
        !Array.isArray(page.items) ||
        typeof page.has_more !== "boolean" ||
        (page.has_more &&
          (!page.next_cursor ||
            page.next_cursor === pageParam ||
            cursors.current.has(page.next_cursor)))
      ) {
        throw new Error("The inquiry changed. Refresh and try again.");
      }
      if (pageParam) cursors.current.add(pageParam);
      return page;
    },
    getNextPageParam: (page) =>
      page.has_more ? (page.next_cursor ?? undefined) : undefined,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    maxPages: 10,
  });
  useEffect(() => {
    if (directory.isError && directory.errorUpdatedAt !== reported.current) {
      reported.current = directory.errorUpdatedAt;
      void onAuthorityError();
    }
  }, [directory.isError, directory.errorUpdatedAt, onAuthorityError]);
  const candidates = new Map<string, InquiryAssigneeListResponse["items"][number]>();
  if (!directory.isError && directory.isFetchedAfterMount) {
    for (const page of directory.data?.pages ?? []) {
      for (const candidate of page.items) {
        if (
          typeof candidate.user_id === "string" &&
          typeof candidate.display_name === "string" &&
          candidate.display_name.trim() &&
          candidate.display_name.trim() !== candidate.user_id
        ) {
          candidates.set(candidate.user_id, candidate);
        }
      }
    }
  }
  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="assignee-picker-title"
    >
      <DialogTitle id="assignee-picker-title">Choose assignee</DialogTitle>
      <DialogContent>
        {directory.isError ? (
          <Alert
            severity="warning"
            action={
              <Button size="small" onClick={onClose}>
                Close
              </Button>
            }
          >
            Assignees are unavailable. Your access may have changed.
          </Alert>
        ) : directory.isPending ? (
          <Typography>Loading assignees…</Typography>
        ) : (
          <Stack spacing={1}>
            {[...candidates.values()].map((candidate) => (
              <Button
                key={candidate.user_id}
                size="small"
                disabled={disabled || directory.isFetching}
                onClick={() => {
                  onAssign(candidate.user_id, revision);
                  onClose();
                }}
              >
                {candidate.display_name}
              </Button>
            ))}
            {!candidates.size && !directory.hasNextPage ? (
              <Typography>No eligible assignees.</Typography>
            ) : null}
            {directory.hasNextPage ? (
              <Button
                size="small"
                disabled={disabled || directory.isFetching}
                onClick={() => void directory.fetchNextPage()}
              >
                Load more
              </Button>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
